import { useState } from "react";

const ENGAGEMENT_BARS = [
  10, 12, 14, 13, 15, 14, 16, 18, 17, 20,
  22, 25, 28, 26, 30, 33, 36, 34, 40, 45,
  50, 55, 52, 60, 68, 75, 72, 85, 96, 90,
  78, 65, 55, 48, 42, 38, 35, 40, 44, 48,
  45, 42, 38, 34, 30, 28, 25, 22, 20, 18,
];

const AI_CUT_POINTS = [7, 14, 20, 27, 35];

export default function SmartTimeline() {
  const [trimStart, setTrimStart] = useState(16);
  const [trimEnd, setTrimEnd] = useState(24);
  const [isDragging, setIsDragging] = useState<"start" | "end" | null>(null);

  const handleBarClick = (index: number) => {
    if (AI_CUT_POINTS.includes(index)) {
      setTrimStart(index);
      setTrimEnd(Math.min(index + 8, ENGAGEMENT_BARS.length - 1));
    }
  };

  const snapToNearest = (pos: number) => {
    const nearest = AI_CUT_POINTS.reduce((prev, curr) =>
      Math.abs(curr - pos) < Math.abs(prev - pos) ? curr : prev
    );
    return Math.abs(nearest - pos) < 3 ? nearest : pos;
  };

  return (
    <div className="bg-[#0F1419] border border-white/8 rounded-xl p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            스마트 타임라인
          </span>
          <div className="flex items-center gap-1.5 bg-[#00D9FF]/10 border border-[#00D9FF]/30 rounded-full px-2 py-0.5">
            <div className="w-1.5 h-1.5 rounded-full bg-[#00D9FF]" />
            <span className="text-[#00D9FF] text-xs font-medium" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              AI 추천 구간
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <span className="text-gray-500">전체: 62:18</span>
          <span className="text-[#00D9FF] font-semibold">
            선택: {Math.round((trimEnd - trimStart) * (62 * 60 + 18) / ENGAGEMENT_BARS.length)}s
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative select-none">
        {/* Engagement heatmap */}
        <div className="flex items-end gap-px h-16 mb-1">
          {ENGAGEMENT_BARS.map((val, i) => {
            const isInRange = i >= trimStart && i <= trimEnd;
            const isAICut = AI_CUT_POINTS.includes(i);
            return (
              <div
                key={i}
                className="flex-1 relative cursor-pointer group rounded-t-sm"
                style={{
                  height: `${val}%`,
                  background: isInRange
                    ? `linear-gradient(180deg, #00D9FF${val > 60 ? "FF" : "99"}, #00D9FF33)`
                    : val > 60
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(255,255,255,0.05)",
                  transition: "background 0.15s",
                }}
                onClick={() => handleBarClick(i)}
              >
                {isAICut && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-px h-full bg-[#F59E0B]/60" />
                )}
              </div>
            );
          })}
        </div>

        {/* Selection overlay + handles */}
        <div className="relative h-5 flex items-center">
          {/* Full bar background */}
          <div className="absolute inset-0 bg-white/4 rounded-full" />

          {/* Selected range */}
          <div
            className="absolute h-full rounded-full"
            style={{
              left: `${(trimStart / ENGAGEMENT_BARS.length) * 100}%`,
              width: `${((trimEnd - trimStart) / ENGAGEMENT_BARS.length) * 100}%`,
              background: "linear-gradient(90deg, #00D9FF44, #10B98144)",
              border: "1px solid rgba(0,217,255,0.3)",
            }}
          />

          {/* AI cut markers */}
          {AI_CUT_POINTS.map((pt) => (
            <div
              key={pt}
              className="absolute w-0.5 h-3 rounded-full cursor-pointer group"
              style={{
                left: `${(pt / ENGAGEMENT_BARS.length) * 100}%`,
                background: "#F59E0B",
                opacity: 0.7,
              }}
              onClick={() => {
                setTrimStart(pt);
                setTrimEnd(Math.min(pt + 8, ENGAGEMENT_BARS.length - 1));
              }}
            >
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#0A0E14] border border-white/10 rounded px-1.5 py-0.5 whitespace-nowrap">
                <span className="text-[#F59E0B] text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>AI 컷포인트</span>
              </div>
            </div>
          ))}

          {/* Start handle */}
          <div
            className="absolute -top-1 w-3 h-7 bg-[#00D9FF] rounded-full cursor-ew-resize z-10 flex items-center justify-center border-2 border-[#0A0E14]"
            style={{ left: `calc(${(trimStart / ENGAGEMENT_BARS.length) * 100}% - 6px)` }}
          >
            <div className="w-0.5 h-3 bg-[#0A0E14]/50 rounded-full" />
          </div>

          {/* End handle */}
          <div
            className="absolute -top-1 w-3 h-7 bg-[#10B981] rounded-full cursor-ew-resize z-10 flex items-center justify-center border-2 border-[#0A0E14]"
            style={{ left: `calc(${(trimEnd / ENGAGEMENT_BARS.length) * 100}% - 6px)` }}
          >
            <div className="w-0.5 h-3 bg-[#0A0E14]/50 rounded-full" />
          </div>
        </div>

        {/* Time labels */}
        <div className="flex justify-between mt-2 text-gray-600 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          <span>00:00</span>
          <span>12:00</span>
          <span>25:00</span>
          <span>38:00</span>
          <span>50:00</span>
          <span>62:18</span>
        </div>
      </div>

      {/* Snap info */}
      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-1.5 text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>
          <span className="w-2 h-2 rounded-full bg-[#F59E0B]" />
          <span>노란 마커 = AI 추천 컷포인트 (클릭해서 스냅)</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500" style={{ fontFamily: "'Inter', sans-serif" }}>
          <span className="w-2 h-2 rounded-full bg-[#00D9FF]" />
          <span>파란 구간 = 현재 선택된 클립</span>
        </div>
      </div>
    </div>
  );
}
